import React from 'react';
import { motion } from 'framer-motion';

interface ProcessProps {
  theme?: 'dark' | 'light' | 'white';
}

const Process: React.FC<ProcessProps> = ({ theme = 'light' }) => {
  const steps = [
    { number: '01', title: 'Consultation', description: 'We discuss your vision, needs, and space requirements.' },
    { number: '02', title: 'Design Development', description: 'Detailed 3D renderings and material selection.' },
    { number: '03', title: 'Precision Craftsmanship', description: 'Built in our workshop with exacting standards.' },
    { number: '04', title: 'Installation', description: 'Professional installation for a perfect fit.' },
  ];

  return (
    <section className="py-12 md:py-20 bg-gray-50 overflow-hidden">
      <div className="container-custom">
        <div className="mb-10 md:mb-16 text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-5xl font-bold text-primary mb-4"
          >
            Our Process
          </motion.h2>
          <motion.div 
            initial={{ opacity: 0, width: 0 }}
            whileInView={{ opacity: 1, width: 96 }}
            viewport={{ once: true }}
            className="h-px bg-accent mx-auto"
          ></motion.div>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {/* Vertical Center Line (Desktop) */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-primary/20 -translate-x-1/2"></div>

          <div className="space-y-6 md:space-y-0">
            {steps.map((step, index) => {
              const isEven = index % 2 === 0;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className={`relative flex flex-col md:flex-row items-center ${
                    isEven ? 'md:flex-row-reverse' : ''
                  }`}
                >
                  {/* Empty space for the other side */}
                  <div className="hidden md:block w-1/2"></div>

                  {/* Marker on Center Line */}
                  <div className="absolute left-1/2 top-8 -translate-x-1/2 flex items-center justify-center w-4 h-4 bg-white border border-primary rounded-full z-10 hidden md:flex">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                  </div>

                  {/* Content Card */}
                  <div className={`w-full md:w-1/2 ${isEven ? 'md:pr-12' : 'md:pl-12'}`}>
                    <div className={`bg-white p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow duration-300 relative overflow-hidden group border-l-4 border-accent ${
                      isEven ? 'md:border-l-0 md:border-r-4' : ''
                    }`}>
                      <div className="flex flex-col">
                        <span className="text-3xl md:text-4xl font-serif text-primary/5 font-bold absolute top-2 right-4 leading-none select-none">
                          {step.number}
                        </span>
                        <h3 className="text-lg md:text-xl font-bold text-primary mb-2 relative z-10">
                          {step.title}
                        </h3>
                        <p className="text-gray-600 font-light leading-relaxed text-sm md:text-base relative z-10">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
